import { useReactiveVar } from '@apollo/client'
import { Box, makeStyles } from '@material-ui/core'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  isCostCalculationSuccessVar,
  selectedCostCalculatorsVar,
  selectedVersionsVar,
} from '../../apollo/reactive-vars'
import { useCostCalculator } from '../../calculators/costs/use-cost-calculator'
import { CommentLocationType } from '../../generated/graphql'
import { isNotNull } from '../../util/array-utility'
import { CommentsButton } from '../comments/CommentsButton'
import CostsMenu from '../costs/CostsMenu'
import { CostsSidebarContent } from '../costs/CostsSidebarContent'
import {
  ProjectSubLayoutProps,
  ProjectSubLayoutParams,
} from './ProjectSubDataLayout'
import { ProjectSubLayoutBase } from './ProjectSubLayoutBase'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

export function CostsLayout({
  children,
}: ProjectSubLayoutProps): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const { id } = useParams<ProjectSubLayoutParams>()
  const selectedVersions = useReactiveVar(selectedVersionsVar)
  const { getCalculatedCosts } = useCostCalculator()

  const pathPrefix = `/projects/${id}/costs`

  const tabs = [
    {
      id: 'costs-table-tab',
      title: t('costs.tabs.table'),
      path: pathPrefix,
    },
    {
      id: 'costs-results-tab',
      title: t('costs.tabs.results'),
      path: `${pathPrefix}/results`,
    },
  ]

  useEffect(
    () =>
      // Clean up on component unmount
      () => {
        // Clean up cost-related reactive vars
        isCostCalculationSuccessVar(false)
        selectedCostCalculatorsVar([])
      },
    [],
  )

  useEffect(() => {
    const unfilteredVersions = selectedVersions.map((version) =>
      getCalculatedCosts(
        version,
        version.energyCalculator,
        version.pricingModel ?? null,
      ),
    )
    if (unfilteredVersions.some((version) => version === null)) {
      isCostCalculationSuccessVar(false)
      selectedCostCalculatorsVar([])
      return
    }

    isCostCalculationSuccessVar(true)
    const versionCostCalculators = unfilteredVersions.filter(isNotNull)
    selectedCostCalculatorsVar(versionCostCalculators)
  }, [selectedVersions, getCalculatedCosts])

  const menu = (
    <Box className={classes.container}>
      <CommentsButton locationType={CommentLocationType.Costs} />
      <CostsMenu />
    </Box>
  )

  return (
    <ProjectSubLayoutBase
      title={t('costs.title')}
      sidebarContent={<CostsSidebarContent />}
      menu={menu}
      tabs={tabs}
    >
      {children}
    </ProjectSubLayoutBase>
  )
}
